/**
 * Cache-Management Utilities für ARCM
 * Behandelt Browser-Cache-Probleme und erzwingt korrektes Laden der Anwendung
 */

export class CacheManager {
  private static readonly RELOAD_FLAGS = {
    APP_INIT: 'appInitRetried',
    CSS_RELOAD: 'cssReloadAttempted',
    GENERAL_RELOAD: 'reloadAttempted'
  };

  /**
   * Prüft ob alle kritischen Ressourcen geladen wurden
   */
  static checkResourcesLoaded(): boolean {
    try {
      // Prüfe ob Vuetify Styles geladen wurden
      const vuetifyLoaded = this.checkVuetifyLoaded();

      // Prüfe ob Vue-App gerendert wurde
      const appRendered = this.checkAppRendered();

      // Prüfe ob kritische CSS-Regeln angewendet wurden
      const cssApplied = this.checkCSSApplied();

      return vuetifyLoaded && appRendered && cssApplied;
    } catch (error) {
      console.error('Fehler beim Prüfen der Ressourcen:', error);
      return false;
    }
  }

  /**
   * Prüft ob Vuetify korrekt geladen wurde
   */
  private static checkVuetifyLoaded(): boolean {
    // Prüfe auf Vuetify CSS
    const stylesheets = Array.from(document.styleSheets);
    return stylesheets.some(sheet => {
      try {
        return sheet.href && (sheet.href.includes('vuetify') ||
               sheet.href.includes('main') ||
               sheet.rules?.length > 0);
      } catch (e) {
        return false;
      }
    });
  }

  /**
   * Prüft ob die Vue-App korrekt gerendert wurde
   */
  private static checkAppRendered(): boolean {
    const appElement = document.getElementById('app');
    if (!appElement) return false;

    // Prüfe auf verschiedene Indikatoren dass Vue geladen ist
    const hasVueContent = !appElement.innerHTML.includes('app-loading');
    const hasVuetifyClasses = appElement.querySelector('.v-application') !== null;
    const hasRouterView = appElement.querySelector('router-view') !== null ||
                          appElement.querySelector('[data-v-]') !== null;
    const hasChildren = appElement.children.length > 0;

    return hasVueContent && hasChildren && (hasVuetifyClasses || hasRouterView);
  }

  /**
   * Prüft ob kritische CSS-Regeln angewendet wurden
   */
  private static checkCSSApplied(): boolean {
    const testElement = document.querySelector('.login-wrapper');
    if (!testElement) return true; // Nicht auf Login-Seite

    const styles = window.getComputedStyle(testElement);
    return !!(styles.background &&
              styles.background !== 'none' &&
              styles.minHeight &&
              styles.minHeight !== 'auto');
  }

  /**
   * Erzwingt einen Reload wenn nötig
   */
  static forceReloadIfNeeded(flagKey: keyof typeof CacheManager.RELOAD_FLAGS, delay: number = 1000): boolean {
    const flag = this.RELOAD_FLAGS[flagKey];
    const reloadCount = parseInt(sessionStorage.getItem('reloadCount') || '0');
    const MAX_RELOADS = 2;

    // Prüfe ob bereits zu oft reloadet wurde
    if (reloadCount >= MAX_RELOADS) {
      return false;
    }

    if (!sessionStorage.getItem(flag)) {
      console.warn(`Cache-Problem erkannt, erzwinge Reload (${flagKey}, Versuch ${reloadCount + 1}/${MAX_RELOADS})...`);
      sessionStorage.setItem(flag, 'true');
      sessionStorage.setItem('reloadCount', (reloadCount + 1).toString());

      setTimeout(() => {
        window.location.reload();
      }, delay);

      return true;
    }

    return false;
  }

  /**
   * Setzt alle Reload-Flags zurück
   */
  static clearReloadFlags(): void {
    Object.values(this.RELOAD_FLAGS).forEach(flag => {
      sessionStorage.removeItem(flag);
    });
    // Reset auch den reload counter
    sessionStorage.removeItem('reloadCount');
  }

  /**
   * Überwacht die App und führt Auto-Reload durch falls nötig
   */
  static setupAutoReload(): void {
    const reloadCount = parseInt(sessionStorage.getItem('reloadCount') || '0');
    const MAX_RELOADS = 2;

    // Stoppe Auto-Reload wenn bereits zu oft versucht
    if (reloadCount >= MAX_RELOADS) {
      return;
    }

    // Überwache App-Loading - längere Wartezeit
    setTimeout(() => {
      if (!this.checkResourcesLoaded()) {
        this.forceReloadIfNeeded('APP_INIT', 500);
      }
    }, 6000); // Längere Wartezeit

    // Reset flags bei erfolgreichem Load
    window.addEventListener('load', () => {
      // Kurze Verzögerung um sicherzustellen dass alles geladen ist
      setTimeout(() => {
        if (this.checkResourcesLoaded()) {
          this.clearReloadFlags();
        }
      }, 2000); // Längere Verzögerung
    });

    // Cleanup bei Page Hide - aber erhalte reload count
    window.addEventListener('pagehide', () => {
      const currentReloadCount = sessionStorage.getItem('reloadCount');
      this.clearReloadFlags();
      if (currentReloadCount && !sessionStorage.getItem('reloadAttempted')) {
        sessionStorage.setItem('reloadCount', currentReloadCount);
      }
    });
  }

  /**
   * Prüft und behebt CSS-spezifische Cache-Probleme
   */
  static fixCSSCacheIssues(): void {
    const reloadCount = parseInt(sessionStorage.getItem('reloadCount') || '0');
    const MAX_RELOADS = 2;

    // Stoppe wenn bereits zu oft reloadet
    if (reloadCount >= MAX_RELOADS) {
      return;
    }

    setTimeout(() => {
      const loginWrapper = document.querySelector('.login-wrapper');
      if (loginWrapper) {
        const styles = window.getComputedStyle(loginWrapper);
        const hasValidBackground = styles.background &&
                                 styles.background !== 'none' &&
                                 !styles.background.includes('rgba(0, 0, 0, 0)');

        if (!hasValidBackground) {
          console.warn('CSS-Cache-Problem erkannt auf Login-Seite');
          this.forceReloadIfNeeded('CSS_RELOAD', 200);
        }
      }
    }, 500); // Längere Wartezeit für CSS
  }

  /**
   * Aktiviert erweiterte Cache-Kontrolle
   */
  static enableAdvancedCacheControl(): void {
    // Verhindere Browser-Cache für kritische Ressourcen
    const meta = document.createElement('meta');
    meta.httpEquiv = 'Cache-Control';
    meta.content = 'no-cache, no-store, must-revalidate, max-age=0';
    document.head.appendChild(meta);

    // Performance Observer für langsame Loads
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            // Prüfe auf langsame CSS/JS Loads
            if (entry.duration > 5000 &&
                (entry.name.includes('.css') || entry.name.includes('.js'))) {
              console.warn(`Langsame Ressource erkannt: ${entry.name} (${entry.duration}ms)`);
            }
          });
        });

        observer.observe({ entryTypes: ['resource'] });
      } catch (e) {
      }
    }
  }
}

// Auto-Setup beim Import - aber nur für erweiterte Cache-Kontrolle
if (typeof window !== 'undefined') {
  // Nur erweiterte Cache-Kontrolle, kein Auto-Reload
  CacheManager.enableAdvancedCacheControl();
}
